import { vec2 } from 'gl-matrix';

import {
  type CanvasEventTarget,
  type CaptureContext,
  type IdCallback,
} from '@canvas/input-types';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';
import {
  type PointerButton,
  type PointerUpCapture,
  type PointerDownCapture,
  type PointerMoveCapture,
  type PointerDropCapture,
  type PointerToolCapture,
  type PointerLeaveCapture,
  type PointerCapture,
  type WheelCapture,
  type PointerOverCapture,
  type PointerDownDoubleCapture,
} from '@canvas/input-types/pointer-events-types';
import { type TouchMoveCapture } from '@canvas/input-types/touch-event-types';
import { vec2extended } from '@canvas/math/vec2';
import { type PreferencesState } from '@canvas/model/preferences';
import { type CanvasDropEvent, type CanvasToolPickEvent } from '@canvas/model/ui';
import { ShortcutKeyMap } from '@canvas/shortcuts/keymap';
import {
  DOMModifierKeys,
  type OperatingSystem,
  type ShortcutModifier,
} from '@canvas/shortcuts/types';

import { getEntityId } from '../input-target/get-entity-id';
import {
  getInitialEditorTarget,
  type GetInitialEditorTargetOptions,
} from '../input-target/get-initial-editor-target';
import { getInputTarget } from '../input-target/get-input-target';

const buttonToType: Record<number, PointerButton> = {
  0: 'none',
  1: 'primary',
  2: 'secondary',
  4: 'middle',
};

/**
 * By default, wheel events are disallowed on all elements.
 *
 * This is to ensure developers opt-in to this behaviour per-target, as input
 * handling is global in the Fabric whiteboard application, and typically
 * represents a panning interaction on the 2D canvas.
 */
const allowedWheelTargets: CanvasEventTarget[] = [
  'ai-agent-dialog',
  'ai-generate-dialog-sources',
  'command-palette-modal',
  'comments-sidebar',
  'comment-thread',
  'database-scrollable-inner-container',
  'dev-ui',
  'editor-popup',
  'editor-typeahead',
  'emoji-picker-container',
  'export-menu',
  'issue-link-menu',
  'jira-import-modal',
  'jira-import-drawer',
  'modal',
  'more-menu',
  'more-menu-submenu',
  'more-shapes-sidebar',
  'preferences-sidebar',
  'popup-menu',
  'stamp-item',
  'sticker-item',
  'template-picker-modal',
  'toolbar-variant-picker',
  'voting-sidebar',
  'private-mode-sidebar',
  'rovo-mini-modal',
  'ai-first-creation-prompt',
  'cloud-architecture-icon-picker',
  'database-drawer-panel',
  'section-action-drawer',
  'slides-filmstrip',
  'slides-navigator',
];
const shouldAllowWheelEvents = (target: CanvasEventTarget) => {
  return allowedWheelTargets.includes(target);
};

/**
 * Returns whether the root should capture future pointer events for this pointer.
 */
const shouldCaptureTarget = (target: CanvasEventTarget) => {
  return (
    target === 'board' || target.startsWith('handle') || target.startsWith('rotate-')
  );
};

/**
 * Adds initial slide-editor target metadata to a captured pointer event when the DOM
 * event hit a slide target.
 *
 * This keeps DOM target discovery in the input layer while leaving the select-tool
 * state machine to decide whether that metadata should select a node or activate text.
 */
const getInitialEditorTargetCapture = (
  evt: MouseEvent | PointerEvent,
  options: GetInitialEditorTargetOptions = {}
) => {
  const initialEditorTarget = getInitialEditorTarget(
    evt.target,
    evt.clientX,
    evt.clientY,
    options
  );

  return initialEditorTarget ? { initialEditorTarget } : {};
};

const syncModifierState = (
  event: MouseEvent,
  os: OperatingSystem,
  onModifierDown: (modifier: ShortcutModifier) => void,
  onModifierUp: (modifier: ShortcutModifier) => void
) => {
  for (const domModifier of DOMModifierKeys) {
    const modifier = ShortcutKeyMap.fromDOMModifier(os, domModifier);

    if (event.getModifierState(domModifier)) {
      onModifierDown(modifier);
    } else {
      onModifierUp(modifier);
    }
  }
};

export type GetUserPreference = <T extends keyof PreferencesState>(
  preference: T
) => PreferencesState[T];

/**
 * Handles returned by {@link setupPointerEvents}.
 *
 * `cleanup` removes every listener (including the canvas wheel listener when
 * attached) and mirrors the previous return value. `attachWheel`/`detachWheel`
 * let callers toggle just the canvas wheel listener at runtime; both are
 * idempotent so repeated calls neither stack nor double-remove the listener.
 */
export type PointerEventsHandles = {
  cleanup: () => void;
  attachWheel: () => void;
  detachWheel: () => void;
};

/**
 * @param root DOM element we listen to for our input events
 * @param stream Record of arrays that will receive all captured events
 * @param captureContext Object used to maintain pointer position and current button
 * @returns Cleanup and wheel attach/detach handles
 */
export const setupPointerEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  os: OperatingSystem,
  onCapture: (
    capture: PointerCapture | TouchMoveCapture,
    preventDefault: () => void
  ) => void,
  captureContext: Pick<
    CaptureContext,
    'x' | 'y' | 'button' | 'touchLocations' | 'lastPointerDown' | 'lastPointerDownExpired'
  >,
  onModifierDown: (modifier: ShortcutModifier) => void,
  onModifierUp: (modifier: ShortcutModifier) => void,
  getCaptureOutsideWindowEnabled: () => boolean,
  getIsRotationEnabled: () => boolean,
  getUserPreference?: GetUserPreference,
  disableCanvasScroll = false
): PointerEventsHandles => {
  const isSafari =
    navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');

  /**
   * Returns the last pointerdown only while it is still eligible to become the matching
   * pointerClick. Pointerup expires it after synthesising click metadata.
   */
  const getLastPointerDown = (): PointerDownCapture | undefined => {
    if (captureContext.lastPointerDownExpired) {
      return undefined;
    }

    return captureContext.lastPointerDown;
  };

  let activePressStartedOnEditorTarget = false;

  const clearActivePressEditorTargetState = () => {
    activePressStartedOnEditorTarget = false;
  };

  const getMouseCoords = (evt: PointerEvent) => {
    const eventX = evt.clientX;
    const eventY = evt.clientY;

    const clientRect = root.getBoundingClientRect();
    const constrainedX = Math.min(Math.max(0, eventX), clientRect.width);
    const constrainedY = Math.min(Math.max(0, eventY), clientRect.height);

    return [constrainedX, constrainedY];
  };

  const isTouch = (evt: PointerEvent) => evt.pointerType === 'touch';

  const onDoubleClick = (evt: MouseEvent) => {
    syncModifierState(evt, os, onModifierDown, onModifierUp);
    const target = getInputTarget(evt.target);
    const capture: PointerDownDoubleCapture = {
      id: id(),
      type: 'pointerDownDouble',
      target,
      ...getInitialEditorTargetCapture(evt),
      properties: {
        x: evt.clientX,
        y: evt.clientY,
        // evt.buttons is 'none' despite 'dblclick' only called when
        // primary button is double clicked. Hardcoded to primary.
        button: 'primary',
        touch: false,
      },
    };

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onPointerDown = (evt: PointerEvent) => {
    syncModifierState(evt, os, onModifierDown, onModifierUp);
    const target = getInputTarget(evt.target);
    const startedOnEditorTarget =
      getInitialEditorTarget(evt.target, evt.clientX, evt.clientY) !== undefined;
    // A direct-edit slide target represents the whole slide child, so normal click
    // selection should stay on the outer entity and show resize handles. Composite
    // slide children still use the target metadata to select the clicked inner node.
    const initialEditorTargetCapture = getInitialEditorTargetCapture(evt, {
      directEditRootSlideTarget: 'ignore',
    });
    const capture: PointerDownCapture = {
      id: id(),
      type: 'pointerDown',
      target,
      properties: {
        x: evt.clientX,
        y: evt.clientY,
        button: buttonToType[evt.buttons],
        touch: isTouch(evt),
      },
      entityId: getEntityId(evt.target),
      ...initialEditorTargetCapture,
    };

    onCapture(capture, evt.preventDefault.bind(evt));

    /**
     * The captureContext x and y is set here to make middle mouse panning
     * work.
     */
    if (!getCaptureOutsideWindowEnabled()) {
      // If a touch location has already been registered, don't update the captureContext
      if (!captureContext.touchLocations.length) {
        captureContext.x = capture.properties.x;
        captureContext.y = capture.properties.y;
      }
    }

    captureContext.lastPointerDown = capture;
    captureContext.lastPointerDownExpired = false;
    captureContext.button = buttonToType[evt.buttons];
    activePressStartedOnEditorTarget = startedOnEditorTarget;

    if (!activePressStartedOnEditorTarget && shouldCaptureTarget(target)) {
      root.setPointerCapture(evt.pointerId);
    }

    if (isTouch(evt)) {
      captureContext.touchLocations.push({
        location: vec2.fromValues(capture.properties.x, capture.properties.y),
        id: evt.pointerId,
      });
    }
  };

  const onPointerUp = (evt: PointerEvent) => {
    syncModifierState(evt, os, onModifierDown, onModifierUp);
    const target = getInputTarget(evt.target);
    const capture: PointerUpCapture = {
      id: id(),
      type: 'pointerUp',
      target,
      properties: {
        x: evt.clientX,
        y: evt.clientY,
        // this pointer event doesn't get a button,
        // we supply it with the last one recorded by pointerMove or pointerDown
        button: captureContext.button,
        touch: isTouch(evt),
      },
      entityId: getEntityId(evt.target),
      ...getInitialEditorTargetCapture(evt, {
        directEditRootSlideTarget: 'ignore',
      }),
    };

    onCapture(capture, evt.preventDefault.bind(evt));

    onPointerClick(evt);

    captureContext.lastPointerDownExpired = true;
    clearActivePressEditorTargetState();

    removeFromTouchLocations(evt);
  };

  const removeFromTouchLocations = (evt: PointerEvent) => {
    if (isTouch(evt)) {
      const closestIndex = captureContext.touchLocations.findIndex(
        (item) => evt.pointerId === item.id
      );
      // Remove the closest pointer down from captureContext
      captureContext.touchLocations.splice(closestIndex, 1);

      // when a finger leaves, we want to update the position with the other finger that's still on screen
      if (captureContext.touchLocations.length === 1) {
        captureContext.x = captureContext.touchLocations[0].location[0];
        captureContext.y = captureContext.touchLocations[0].location[1];
      }
    }
  };

  const onPointerMove = (evt: PointerEvent) => {
    syncModifierState(evt, os, onModifierDown, onModifierUp);

    // Re-acquire pointer capture lost when the pointer left the browser window
    // during an active drag, preventing rotation handles from intercepting events.
    if (
      getIsRotationEnabled() &&
      getCaptureOutsideWindowEnabled() &&
      !root.hasPointerCapture(evt.pointerId) &&
      !activePressStartedOnEditorTarget &&
      shouldCaptureTarget(getInputTarget(evt.target))
    ) {
      root.setPointerCapture(evt.pointerId);
    }

    const [x, y] = getMouseCoords(evt);

    const target = getInputTarget(evt.target);

    // Panning and zooming is handled differently for single mouse events and multitouch
    // events. Mouse events are emitted as PointerMoveCapture, and multitouch events are
    // emitted as TouchMoveCapture. A multitouch event is classified as a PointerEvent
    // which has pointerType as touch, and occurs when there are multiple touch locations
    // recorded (captureContext.touchLocations). Multitouch events will result in both
    // pan and zoom behaviour.
    const numTouchPoints = captureContext.touchLocations.length;
    if (isTouch(evt) && numTouchPoints >= 2) {
      // Each touch point registers its own pointer, which triggers its own independent
      // click and move events. We keep track of them in the captureContext
      // touchLocations which is updated during pointerup and pointerdown events.
      // Each pointermove event has the pointerId in it, so we work out which index the
      // current pointer event's pointerId corresponds to, as we manipulate it in place
      // when new information is available about its location.
      const indexForPointerId = captureContext.touchLocations.findIndex(
        (item) => item.id === evt.pointerId
      );

      // Should the index lookup fail we need to return. This shouldn't happen, unless a
      // mouse event has accidentally been classified as a touch event (which it
      // shouldn't).
      // Long term we can probably harmonise both touch and mouse behaviour, but for the
      // moment we'll keep it separate.
      if (indexForPointerId < 0) {
        return;
      }

      // Calculate the midpoint between all of the touch points based on their previous
      // location
      const previousMidpoint = vec2extended.findAveragePositionOfElements(
        captureContext.touchLocations.map((l) => l.location)
      );

      // Calculate the distance between the touch points based on their previous location
      const previousDistance = vec2.dist(
        captureContext.touchLocations[0].location,
        captureContext.touchLocations[1].location
      );

      // Update the co-ordinate for the current pointerId in place
      captureContext.touchLocations[indexForPointerId].location = vec2.fromValues(x, y);

      // Calculate the midpoint between all of the touch points based on their updated
      // location
      const afterMidpoint = vec2extended.findAveragePositionOfElements(
        captureContext.touchLocations.map((l) => l.location)
      );

      // Calculate the distance between the touch points based on their updated location
      const afterDistance = vec2.dist(
        captureContext.touchLocations[0].location,
        captureContext.touchLocations[1].location
      );

      // Calculate the percentage change between the distances to get the percentage to
      // scale by. This is used to determine how much to zoom by. We ignore small
      // distances to prevent a divide by zero.
      const deltaDistance =
        previousDistance < 1 || afterDistance < 1 ? 1 : afterDistance / previousDistance;

      // Calculate the delta between the midpoints to get the amount to pan by.
      const deltaX = afterMidpoint[0] - previousMidpoint[0];
      const deltaY = afterMidpoint[1] - previousMidpoint[1];

      const touchCapture: TouchMoveCapture = {
        id: id(),
        type: 'touchMove',
        target,
        // I'm not sure these properties are used in the way they should be.
        // For panning, the deltaX/Y are aggregated into the `lastCapture`
        properties: {
          pointerId: evt.pointerId,
          x, // Panning doesn't use this value
          y, // Panning doesn't use this value
          deltaX, // Panning uses this value
          deltaY, // Panning uses this value
          button: buttonToType[evt.buttons],
          touch: isTouch(evt),
          locations: captureContext.touchLocations,
          deltaDistance,
          activeTouchPoints: numTouchPoints,
        },
      };

      onCapture(touchCapture, evt.preventDefault.bind(evt));
    } else {
      const capture: PointerMoveCapture = {
        id: id(),
        type: 'pointerMove',
        target,
        properties: {
          x,
          y,
          deltaX: captureContext.x === undefined ? 0 : x - captureContext.x,
          deltaY: captureContext.y === undefined ? 0 : y - captureContext.y,
          button: buttonToType[evt.buttons],
          touch: isTouch(evt),
        },
        entityId: getEntityId(evt.target),
      };

      captureContext.button = buttonToType[evt.buttons];
      onCapture(capture, evt.preventDefault.bind(evt));

      captureContext.x = x;
      captureContext.y = y;
    }
  };

  const onPointerClick = (evt: PointerEvent) => {
    const lastPointerDown = getLastPointerDown();
    if (!lastPointerDown) {
      return;
    }

    const reached = vec2.fromValues(evt.clientX, evt.clientY);
    const isNearby =
      vec2.dist(
        vec2.fromValues(lastPointerDown.properties.x, lastPointerDown.properties.y),
        reached
      ) < 4;

    if (isNearby) {
      const initialEditorTarget =
        getInitialEditorTarget(evt.target, evt.clientX, evt.clientY, {
          directEditRootSlideTarget: 'text',
        }) ??
        (lastPointerDown.initialEditorTarget &&
        lastPointerDown.initialEditorTarget.targetType === 'slideTarget'
          ? {
              ...lastPointerDown.initialEditorTarget,
              clientX: evt.clientX,
              clientY: evt.clientY,
            }
          : undefined);

      onCapture(
        {
          // We want the pointerClick to be tracked by the pointerDown that caused it
          id: lastPointerDown.id,
          type: 'pointerClick',
          target: getInputTarget(evt.target),
          properties: {
            x: evt.clientX,
            y: evt.clientY,
            button: lastPointerDown.properties.button,
            touch: isTouch(evt),
          },
          entityId: getEntityId(evt.target),
          ...(initialEditorTarget ? { initialEditorTarget } : {}),
        },
        evt.preventDefault.bind(evt)
      );
    }
  };

  const onPointerDrop = (evt: CanvasDropEvent) => {
    const target = getInputTarget(evt.target);
    const capture: PointerDropCapture = {
      id: id(),
      type: 'pointerDrop',
      target,
      properties: {
        ...evt.detail,
        x: evt.detail.dropX,
        y: evt.detail.dropY,
      },
    };

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onPointerTool = (evt: CanvasToolPickEvent) => {
    const target = getInputTarget(evt.target);
    const capture: PointerToolCapture = {
      id: id(),
      type: 'pointerTool',
      target,
      properties: evt.detail,
    };

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onPointerOver = (evt: PointerEvent) => {
    const target = getInputTarget(evt.target);

    const capture: PointerOverCapture = {
      id: id(),
      target,
      type: 'pointerOver',
      properties: {
        x: evt.clientX,
        y: evt.clientY,
        // this pointer event doesn't get a button,
        // we supply it with the last one recorded by pointerMove or pointerDown
        button: captureContext.button,
        touch: isTouch(evt),
      },
    };

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onPointerLeave = (evt: PointerEvent) => {
    const capture: PointerLeaveCapture = {
      id: id(),
      target: 'none',
      type: 'pointerLeave',
      properties: {
        x: evt.clientX,
        y: evt.clientY,
        // this pointer event doesn't get a button,
        // we supply it with the last one recorded by pointerMove or pointerDown
        button: captureContext.button,
        touch: isTouch(evt),
      },
    };

    /**
     * The captureContext x and y is reset here to make middle mouse panning
     * work.
     */
    if (!getCaptureOutsideWindowEnabled()) {
      captureContext.x = undefined;
      captureContext.y = undefined;
    }

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onMouseWheel = (evt: WheelEvent) => {
    const target = getInputTarget(evt.target);

    const isSpecialKeyPressed = evt.ctrlKey || evt.metaKey;
    const shouldPassthroughBoardBehaviour = target === 'board';

    const allowWheelEvents = shouldAllowWheelEvents(target);
    if (isSpecialKeyPressed || shouldPassthroughBoardBehaviour || !allowWheelEvents) {
      evt.preventDefault();
    }

    let isMouse = false;
    // allowWheelEvents tells us that the target requires wheel events to be
    // captured within the target, e.g. the template picker modal. In this
    // instance, we don't want to provide any panning or zooming, hence we skip
    // checking if a user wants to zoom on mouse scroll.
    if (!allowWheelEvents) {
      isMouse = getUserPreference?.('scrollingPreference') ?? false;
    }

    const modifier = (() => {
      if (evt.ctrlKey) {
        return 'Control';
      }
      if (evt.metaKey) {
        return 'Meta';
      }
      if (evt.shiftKey) {
        return 'Shift';
      }
      // If the user's preferences OR their auto-detected peripheral is a mouse,
      // we need to return `Control` to provide them a mouse-scrolling experience.
      if (isMouse) {
        return 'Control';
      }
      return undefined;
    })();

    // Do not send wheel events to the input machine for allowlisted targets so that
    // only the element scrolls (e.g. filmstrip, modal) and the canvas does not react.
    if (allowWheelEvents) {
      return;
    }

    const capture: WheelCapture = {
      id: id(),
      target,
      type: 'wheel',
      properties: {
        deltaY: evt.deltaY,
        deltaX: evt.deltaX,
        modifier,
        // There's a known issue with Safari's handling of 'wheel' events within iframes
        // when using a trackpad. In these cases, the x/y coordinates reported are
        // relative to the parent window, not the iframe itself, leading to inaccurate
        // pointer positions. This differs from actual mouse wheel events, where the
        // reported coordinates are correctly relative to the iframe.

        // Unfortunately, there's no way to correct Safari's behavior in these cases. Our
        // workaround is to use the last reported position from a 'pointerMove' event
        // instead, as these events always have the correct coordinates.
        x: isSafari ? captureContext.x ?? evt.clientX : evt.clientX,
        y: isSafari ? captureContext.y ?? evt.clientY : evt.clientY,
      },
    };

    onCapture(capture, evt.preventDefault.bind(evt));
  };

  const onPointerCancel = (evt: PointerEvent) => {
    clearActivePressEditorTargetState();
    removeFromTouchLocations(evt);
  };

  // The canvas wheel listener is toggleable at runtime (e.g. when a native
  // embed maximizes). Track its attached state so attach/detach stay idempotent
  // and always re-use the same `{ passive: false }` contract as the load-time
  // attach, keeping scroll/zoom behaviour identical when re-enabled.
  let wheelAttached = false;
  const attachWheel = () => {
    if (wheelAttached) {
      return;
    }
    root.addEventListener('wheel', onMouseWheel, { passive: false });
    wheelAttached = true;
  };
  const detachWheel = () => {
    if (!wheelAttached) {
      return;
    }
    root.removeEventListener('wheel', onMouseWheel);
    wheelAttached = false;
  };

  if (!disableCanvasScroll) {
    attachWheel();
  }
  root.addEventListener('pointerover', onPointerOver);
  root.addEventListener('pointerleave', onPointerLeave);
  root.addEventListener('pointerup', onPointerUp);
  root.addEventListener('pointermove', onPointerMove);
  root.addEventListener('dblclick', onDoubleClick);
  root.addEventListener('pointerdown', onPointerDown);
  root.addEventListener('pointercancel', onPointerCancel);
  root.addEventListener('canvas:drop', onPointerDrop);
  root.addEventListener('canvas:tool', onPointerTool);

  const cleanup = () => {
    detachWheel();
    root.removeEventListener('pointerover', onPointerOver);
    root.removeEventListener('pointerleave', onPointerLeave);
    root.removeEventListener('pointerup', onPointerUp);
    root.removeEventListener('dblclick', onDoubleClick);
    root.removeEventListener('pointermove', onPointerMove);
    root.removeEventListener('pointerdown', onPointerDown);
    root.removeEventListener('pointercancel', onPointerCancel);
    root.removeEventListener('canvas:drop', onPointerDrop);
    root.removeEventListener('canvas:tool', onPointerTool);
  };

  return { cleanup, attachWheel, detachWheel };
};
